import type { AdminQuoteGuest } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { GUEST_CONTACT_METHOD_LABELS } from '@/entities/quote';
import { GuestQuoteLinkButton, RevealPhoneButton } from '@/features/quote';
import { formatDateTime } from '@/shared/lib';
import { ContentCard, cn, StatusBadge, detailListStyles as styles } from '@/shared/ui';

export interface QuoteContactSectionProps {
  readonly quoteRequestId: string;
  /** users.email — 바로 보인다. 아직 회원 계정이 없는 견적(비회원 접수)은 null 이고 '-'로 보인다. 휴대폰은 보기 버튼으로만(복호화·감사 로그). */
  readonly email: string | null;
  /** 비회원 접수 요약(비회원 견적 설계 G14). 회원이 요청한 견적은 null. */
  readonly guest: AdminQuoteGuest | null;
}

/** '연락 방법 이메일 · 회원 계정 연결 전' 처럼 비회원 접수의 연락 방법과 회원 계정 연결 여부·시각. */
const guestOriginText = (guest: AdminQuoteGuest): string => {
  const method = `연락 방법 ${GUEST_CONTACT_METHOD_LABELS[guest.contactMethod]}`;

  if (!guest.claimed) return `${method} · 회원 계정 연결 전`;

  return guest.claimedAt === null
    ? `${method} · 회원 계정 연결됨`
    : `${method} · 회원 계정 연결됨 · ${formatDateTime(guest.claimedAt)}`;
};

/**
 * 고객 연락처(견적 설계 §6-2, 비회원 견적 설계 G14).
 * 회원 견적과 회원 계정에 연결된 비회원 견적은 회원 이메일과 휴대폰 보기(본인인증 번호)를 보여 준다.
 * 연결 전 비회원 견적은 고객이 고른 연락처 하나만 보여 준다. 이메일은 바로 보이고, 휴대폰은 보기 버튼으로만 보인다
 * (서버가 견적에 남긴 번호의 암호문을 복호화하고 접속 기록을 남긴다).
 * 비회원 접수 견적은 연결 뒤에도 '비회원 접수' 표시와 [링크 복사]를 남긴다.
 */
export const QuoteContactSection = ({
  quoteRequestId,
  email,
  guest,
}: QuoteContactSectionProps): ReactElement => {
  // 연결 전 비회원 견적만 고객이 고른 연락처 하나로 좁힌다.
  const pendingGuest = guest !== null && !guest.claimed ? guest : null;
  const showsEmail = pendingGuest === null || pendingGuest.contactMethod === 'EMAIL';
  const showsPhone = pendingGuest === null || pendingGuest.contactMethod === 'PHONE';
  const shownEmail = pendingGuest === null ? email : pendingGuest.email;

  return (
    <ContentCard title="고객 연락처">
      <dl className={cn(styles.list, 'items-center')}>
        {guest === null ? null : (
          <>
            <dt className={styles.term}>접수</dt>
            <dd className="flex flex-wrap items-center gap-2">
              <StatusBadge tone="warning">비회원 접수</StatusBadge>
              <span className="text-xs text-muted-foreground">{guestOriginText(guest)}</span>
            </dd>
          </>
        )}
        {showsEmail ? (
          <>
            <dt className={styles.term}>이메일</dt>
            <dd className="wrap-anywhere">
              {shownEmail ?? <span className={styles.empty}>-</span>}
            </dd>
          </>
        ) : null}
        {showsPhone ? (
          <>
            <dt className={styles.term}>휴대폰</dt>
            <dd>
              <RevealPhoneButton quoteRequestId={quoteRequestId} />
            </dd>
          </>
        ) : null}
        {guest === null ? null : (
          <>
            <dt className={styles.term}>견적 링크</dt>
            <dd>
              <GuestQuoteLinkButton quoteRequestId={quoteRequestId} claimed={guest.claimed} />
            </dd>
          </>
        )}
      </dl>
    </ContentCard>
  );
};
